import { cn } from '@attendance/ui/utils';

/* ===========================================================================
 * THE RAIL'S SKIN — the icon scale, the inset, the row, the eyebrow.
 *
 * Every value the sidebar draws itself with, in one place and out of
 * `Sidebar.tsx`, which is long-standing size debt behind a ratchet: an
 * allowlisted file may shrink and never grow. These constants are a coherent
 * unit with one reason to change (the rail's geometry), so they move together
 * rather than a row at a time. The reasoning that came with each one is
 * unchanged below — it is the record of what each value was before, and why the
 * previous one was wrong.
 *
 * The contrast measurements these alphas answer to are in the header of
 * `Sidebar.tsx`, beside the rail's own tokens.
 * ======================================================================== */

/**
 * ONE SIZE, ONE WEIGHT — for every glyph on the rail, spread from here.
 *
 * Lucide draws on a 24px grid at `strokeWidth` 2, so the stroke a browser
 * actually paints is `2 × size / 24`: the same icon at 14px lands at 1.17px and
 * at 18px at 1.50px. Size and weight are therefore ONE decision, not two, and
 * the group-disclosure chevron — the single glyph here that was set to 14 —
 * was not a smaller icon so much as a thinner one, sitting in the same row as
 * an 18px section icon. Both numbers are declared, so a future icon cannot
 * quietly inherit a different weight by picking a different size.
 *
 * Spread (`{...NAV_ICON}`) rather than wrapped in a component: `NavLeaf.icon`
 * is a `LucideIcon` and the rail also renders fixed glyphs (chevrons, the close
 * X, the surface-switch arrow), so a spread is the one form that covers both
 * without a second indirection.
 */
export const NAV_ICON = { size: 17, strokeWidth: 2 } as const;

/**
 * The SECTION-HEADING glyph, which is deliberately not `NAV_ICON`.
 *
 * `Hadir Dashboard.dc.html` draws the eyebrow's mark at 13px and stroke 2.4
 * against the rows' 17px at stroke 2 — smaller, and compensating for the loss
 * of weight at that size by drawing heavier. That is the same relationship the
 * eyebrow's TYPE has to a row's label, so the glyph follows the type rather
 * than staying row-sized while the words shrink beside it.
 *
 * This is the one sanctioned second size on the rail. `Sidebar.test.tsx` still
 * pins BOTH tiers so a third cannot appear by accident.
 */
export const HEADING_ICON = { size: 13, strokeWidth: 2.4 } as const;

/**
 * How a row's LABEL behaves once the rail is collapsed to icons.
 *
 * `md:hidden` — what this used to be — is `display: none`, which takes the text
 * out of the accessibility tree along with the pixels. The rows survived that
 * because each carries a `title`, and a title is a last-resort accessible name;
 * the check-in CTA had no title, so the app's primary action became a link with
 * no name at all the moment somebody collapsed the rail.
 *
 * `sr-only` clips the label to a 1px box instead. It is out of flow (position:
 * absolute), so it costs no width and contributes no flex gap — the row looks
 * identical — but it is still text, so every destination keeps a real
 * accessible name in every state, from one rule rather than per-row `aria-label`
 * patches. Only `md:` is touched: the mobile drawer is never collapsed.
 */
export const COLLAPSED_LABEL = 'md:sr-only';

/** Divider/hairline on the rail — under the rail head, and above every group.
 * It draws the group separators at every width now, not only while the rail is
 * collapsed: with the section heading demoted to a quiet eyebrow, this line is
 * what says where one section ends.
 *
 * /22 rather than /15: the old value was tuned against a saturated surface, and
 * a hairline that read on violet disappears on ink. Decorative, so no contrast
 * floor applies — it just has to be seen. */
/* 22 is not a step on Tailwind's opacity scale and bare numbers are not
 * arbitrary values, so `/22` compiled to NOTHING and the divider never drew.
 * Compile-probed against the real config: `/20` and `/[22%]` both emit, `/22`
 * does not. Taking `/20` — the scale step — rather than pinning an arbitrary
 * 22% nobody would have missed. */
export const RAIL_DIVIDER = 'border-nav-border';

/**
 * Focus ring for anything sitting ON the rail, drawn in the paired ink so it
 * stays visible in both themes and for any tenant brand.
 *
 * The 2px offset used to be the risky part — on a full-bleed row it had nowhere
 * to go but into the neighbouring row. Rows are inset pills now, so the ring has
 * the rail's own 12px of inline room to sit in and it traces each pill's corner
 * radius, which is the shape a focused row actually has.
 */
export const RAIL_FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

/** A 44px-square icon button on the rail (WCAG 2.5.5 target size) — declared
 * as a MINIMUM on both axes rather than a fixed 44×44 box, so it can never be
 * squeezed under the floor by a neighbour in the rail head. `[@media
 * (pointer:coarse)]`, matching `IconButton.tsx`'s own coarse-pointer floor —
 * a collapse control on a touch tablet is not a mouse-only affordance, but a
 * blanket 44px on a MOUSE made the whole rail read visibly looser than the
 * handoff at every row, not only this one. The handoff draws this button at
 * 28px, which is what a fine pointer now gets back. */
export const RAIL_ICON_BUTTON = cn(
  '[@media(pointer:coarse)]:min-h-touch [@media(pointer:coarse)]:min-w-touch shrink-0 items-center justify-center rounded-control text-nav-on-surface-muted',
  'transition-colors duration-fast hover:bg-nav-on-surface/10 hover:text-nav-on-surface',
  RAIL_FOCUS
);

/**
 * THE RAIL'S OWN INSET, and why rows are pills now.
 *
 * Rows used to be full-bleed — edge to edge, no radius — with a 2px logical
 * border on the inline-start side that was transparent until the row was
 * current. That was the right shape for a 240px rail whose active row had to be
 * found in a saturated slab. The reskin gives the rail 12px of inline breathing
 * room and rounds each row to `--radius-control`, so a row reads as an object
 * you can point at rather than a band across the whole column, and the current
 * one is a filled pill instead of a full-width stripe.
 *
 * The inset lives on the ONE scrolling container rather than on each row, group
 * and pill wrapper, so the rail has a single answer to "how far in does a row
 * start" — and the group hairlines inset with it, which is what makes them read
 * as dividers inside the rail instead of scars across it.
 *
 * Collapsed, it shrinks to 4px: at 52px wide, 12px either side of a 44px target
 * leaves 28px, and the glyph stops being centred on anything.
 */
// 14px, the handoff's own rail inset (`padding: 18px 14px 14px` on the nav,
// `Hadir Dashboard.dc.html:32`). It was `ps-3 pe-3` — 12px — which pulled every
// row, pill and hairline 2px tighter to the edge than the design draws them.
export const RAIL_INSET = 'ps-3.5 pe-3.5';
export const RAIL_INSET_COLLAPSED = 'md:ps-1 md:pe-1';

/**
 * Row geometry, shared by every destination on the rail: an inset pill at
 * `--radius-control`, 44px minimum height, and `relative` so the current row can
 * carry its marker (see `ActiveMarker`) without the marker taking part in
 * layout — nothing shifts when the highlight moves. `ps`/`pe` throughout, never
 * `pl`/`pr`: this ships in Arabic.
 *
 * `collapsed` is a DESKTOP-only state. The mobile drawer is always full width,
 * so collapse is expressed purely through `md:` classes and the same DOM serves
 * both — no second render path, no behaviour that differs by viewport.
 *
 * THREE SIGNALS mark the current row, because the rail has two shapes and the
 * label is only present in one of them: the brand bar on the inline-start edge,
 * a lifted background, and the glyph itself in `--nav-active` (see `Leaf`). The
 * heavier label is a fourth — and it is the one that disappears when the rail
 * collapses to 52px, which is why it cannot be the only one.
 *
 * Hover and the current row share the same `/10` lift, and that is not an
 * oversight: the handoff separates them by two percent (.07 hover, .09 current),
 * which is below the granularity of the opacity scale this file already argued
 * itself into using rather than pinning arbitrary percentages. The three signals
 * above are what tell the two states apart, not a 2% difference in ground.
 */
export function rowClasses(opts: { collapsed: boolean; active?: boolean }): string {
  return cn(
    // `[@media(pointer:coarse)]`, not a blanket `min-h-touch` — see the note
    // on `RAIL_ICON_BUTTON`. Forty-odd rows each carrying a mouse-only
    // tablet's touch floor is what made the whole rail read visibly looser
    // than the handoff; `py-2.5` (10px) alone gives a fine pointer the
    // handoff's own ~35px row, and a coarse pointer still gets 44px.
    // `py-[9px]` is the handoff's own row padding (`padding: 9px 10px`), which
    // with a 17px glyph and 13.5px label lands the ~37px row it draws. `py-2.5`
    // (10px) was a whole step of the spacing scale wider on every one of forty
    // rows, which is most of why the rail read looser than the mock.
    'relative flex [@media(pointer:coarse)]:min-h-touch w-full items-center gap-2.5 rounded-control py-rail-row',
    'ps-2.5 pe-2.5 text-start text-copy',
    'transition-colors duration-fast',
    RAIL_FOCUS,
    opts.collapsed && 'md:justify-center md:ps-0 md:pe-0',
    opts.active
      ? 'bg-nav-on-surface/10 font-semibold text-nav-on-surface'
      : 'font-medium text-nav-on-surface-muted hover:bg-nav-on-surface/10 hover:text-nav-on-surface'
  );
}

/**
 * THE SECTION EYEBROW — type, colour and inset, shared by both kinds of heading
 * the rail draws.
 *
 * 12px uppercase at +0.3px tracking (`text-eyebrow`), the one step in the system
 * allowed to be uppercase and the smallest word this app may set: §1 of
 * docs/design-rules.md bans 11px for anything read as a word, which is what the
 * handoff sets these at. `/60` against rows at `/80`, so a heading is audibly
 * quieter than the destinations under it — see the contrast note at the top.
 *
 * GEOMETRY IS NOT SHARED, deliberately. The admin sections collapse, so their
 * heading is a button and answers to the 44px target floor; the personal
 * heading names a list that does not collapse, so it is text and a 44px text
 * box would be a control-sized thing nothing happens when you press. One voice,
 * two shapes.
 */
/* Geometry is the handoff's: `gap: 8px; padding: 0 8px 6px` — no space above
   the heading at all (the group's own `pt` already put it there) and 6px under
   it, so the words sit tight to the rows they name. It was `ps-2.5 pe-2.5`
   plus a `py-2` at each call site, which made a 34px block for a 12px word. */
export const EYEBROW = 'flex items-center gap-element rounded-control ps-2 pe-2 pb-1.5 text-eyebrow';
